import React, { useCallback, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { postData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

// Utility function to format dates
const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

function SubCategoryDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);

  // Fetch insurance plans from API
  const fetchInsurancePlans = async () => {
    try {
      setLoading(true);
      const response = await postData('api/insurance-plans/list', {
        page: 1,
        limit: 10,
      });
      if (response?.plans) {
        setPlans(response.plans);
      } else {
        throw new Error(response?.message || "Failed to fetch insurance plans");
      }
    } catch (error) {
      console.error("Fetch insurance plans error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update insurance plan
  const updateInsurancePlan = async (planId, updatedData) => {
    try {
      setLoading(true);
      const response = await postData('api/insurance-plans/update', {
        id: planId,
        data: updatedData,
      });
      if (response?.success) {
        
        
      } else {
        throw new Error(response?.message || "Failed to update insurance plan");
      }
    } catch (error) {
      console.error("Update insurance plan error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsurancePlans();
  }, []);

  const selectedSubcategory = plans.find(plan => plan._id === id);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <CustomLoader loading={loading} />
      </div>
    );
  }

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          {/* Page Header */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-title-box">
                <h4 className="mb-1">Subcategory Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">Subcategory Details</li>
                  <li className="breadcrumb-item active">Plan Name: {selectedSubcategory?.name || "N/A"}</li>
                </ol>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </div>

          {/* Subcategory Details */}
          {selectedSubcategory ? (
            <div className="card mb-4 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <h4 className="me-3">Plan Name:</h4> <h4>{selectedSubcategory.name}</h4>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <h4 className="me-3">Insurer Logo:</h4>
                  <img src={selectedSubcategory.insurerLogo} alt={selectedSubcategory.name} width="100" className="border rounded" />
                </div>
                <div className="d-flex align-items-center mb-3">
                  <strong className="me-3">Company Name:</strong> <span>{selectedSubcategory.company.name}</span>
                </div>
                <div className="mb-3">
                  <strong>Category:</strong> <span>{selectedSubcategory.category.title}</span><br/>
                  <strong>Minimum Investment Years:</strong> <span>{selectedSubcategory.minInvestYears || "N/A"}</span><br/>
                  <strong>Ideal Withdrawal Years:</strong> <span>{selectedSubcategory.idealWithdrawYears || "N/A"}</span>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <strong className="me-3 mb-0">Special Offer: </strong><span>{selectedSubcategory.specialOffer.text}</span>
                  <div className="d-flex align-items-center ms-3">
                    <span className="me-2">BG Color:</span>
                    <div className="me-2 border rounded" style={{ backgroundColor: selectedSubcategory.specialOffer.bgGradient, width: '50px', height: '20px' }}></div>
                    <span style={{ fontSize: '12px' }}>{selectedSubcategory.specialOffer.bgGradient}</span>
                  </div>
                  <div className="d-flex align-items-center ms-3">
                    <span className="me-2">Text Color:</span>
                    <div className="me-2 border rounded" style={{ backgroundColor: selectedSubcategory.specialOffer.textColor, width: '50px', height: '20px' }}></div>
                    <span style={{ fontSize: '12px' }}>{selectedSubcategory.specialOffer.textColor}</span>
                  </div>
                </div>
                <div className="mb-3">
                  <strong>Stats:</strong>
                  <ul className="list-unstyled">
                    {selectedSubcategory.stats.map(stat => (
                      <li key={stat._id} className="mb-1">
                        <strong>Label:</strong> <span className="me-3">{stat.label}</span>
                        <strong>Value:</strong> <span>{stat.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mb-3">
                  <strong>Features:</strong>
                  <ul className="list-unstyled">
                    {selectedSubcategory.features.map(feature => (
                      <li key={feature._id} className="mb-1 d-flex align-items-center">
                        <strong className="me-2">Text:</strong> <span>{feature.text}</span>
                        <div className="d-flex align-items-center ms-3">
                          <span className="me-2">Color:</span>
                          <div className="me-2 border rounded" style={{ backgroundColor: feature.color, width: '50px', height: '20px' }}></div>
                          <span style={{ fontSize: '12px' }}>{feature.color}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mb-3">
                  <strong>Status:</strong> <span>{selectedSubcategory.isActive ? "Active" : "Inactive"}</span><br/>
                  <strong>Created At:</strong> <span>{formatDate(selectedSubcategory.createdAt)}</span><br/>
                  <strong>Updated At:</strong> <span>{formatDate(selectedSubcategory.updatedAt)}</span>
                </div>
                <div className="d-flex gap-2 mb-3">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() =>
                      navigate(`/update-subcategory`, {
                        state: { subCategoryData: selectedSubcategory },
                      })
                    }
                  >
                    <i className="mdi mdi-pencil"></i>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <p>No subcategory found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubCategoryDetails;